import { ProjectActionType } from './enums';
import { ProjectState, ProjectAction } from './interfaces';
import type { BuilderUserTier } from '@components/BuilderTierAccess';

export const initialState: ProjectState = {
	quotationData: {},
	preDesignQuotation: {},
	defaultQuotations: {},
	defaultQuotationsLoading: false,
	preDesignQuotationLoading: false,
	loading: false,
	error: false,
	leadId: '',
	projectId: '',
	projectName: '',
	leadName: '',
	sessionID: '',
	userDetails: {},
	quotations: [],
	floorPlanDocURL: '',
	predictionId: '',
	predictionStatus: null,
	isPolling: false,
	predictionData: [],
	budget: '',
	floorPlanImages: [],
	selectedQuotation: {},
	quotationsLoading: false,
	createQuotationLoading: false,
	projectDetails: {},
	projectDetailsLoading: false,
	projectDetailsError: null,
	projectActivities: [],
	projectActivitiesLoading: false,
	projectActivitiesError: { code: '', message: '', type: '' },
	projectActivityDocuments: [],
	projectActivityDocumentsLoading: false,
	projectActivityDocumentsError: { code: '', message: '', type: '' },
	projectCurrentActivity: {},
	projectsListLoading: true,
	projectsList: [],
	projectsListError: false,
	projectTotalRecords: 0,
	productCategories: [],
	productCategoriesLoading: false,
	productCategoriesError: false,
	twoDJson: {},
	twoDJsonLoading: false,
	twoDJsonError: false,
	threeDJson: {},
	threeDJsonByProjectId: {} as Record<string, any>, // Store 3D JSON per projectId
	threeDJsonLoading: false,
	threeDJsonError: false,
	leadProjectsList: [],
	leadProjectsListLoading: false,
	areaList: [],

	paymentSlipsList: [],
	paymentSlipsListLoading: false,
	paymentSlipsListError: {},

	ledgerData: {},
	ledgerDataLoading: false,
	ledgerDataError: { code: '', message: '', type: '' },

	isPaymentLoading: false,
	paymentList: [],
	warrantyClaims: [],
	warrantyClaimsLoading: false,
	warrantyClaimsError: null,
	warrantyClaimsTotalRecords: 0,

	warranties: [],
	warrantiesLoading: false,
	warrantiesError: null,
	warrantiesTotalRecords: 0,

	// Warranty code details (scopes by code)
	warrantyCodeDetails: [],
	warrantyCodeDetailsLoading: false,
	warrantyCodeDetailsError: null,

	warrantyClaimDetails: null,
	warrantyClaimDetailsLoading: false,
	warrantyClaimDetailsError: null,

	cutListData: [],
	cutListDataLoading: false,
	cutListDataError: {},

	optimiserData: {},
	optimiserDataLoading: false,
	optimiserDataError: null,

	assignedProjectIds: [],
	assignedProjectsLoading: false,
	assignedProjectsError: null,

	builderStates: [],
	builderStatesLoading: false,
	builderStatesError: null,

	promoCodeHistory: [],
	promoCodeHistoryLoading: false,
	promoCodeHistoryError: null,

	promoCodeAmount: [],
	promoCodeAmountLoading: false,
	promoCodeAmountError: null,

	projectsOrders: [],
	projectsOrdersLoading: false,
	projectsOrdersError: null,

	userTier: 'advanced' as BuilderUserTier,
	stylePreferences: {
		colorMood: null as string | null,
		styleMood: null as string | null
	}
};

export default (state = initialState, { type, payload }: ProjectAction): ProjectState => {
	switch (type) {
		case ProjectActionType.ADD_PROJECT_DETAILS:
			return {
				...state
			};

		case ProjectActionType.GET_PRE_DESIGN_QUOTATION:
			return {
				...state,
				preDesignQuotationLoading: true,
				preDesignQuotation: {}
			};

		case ProjectActionType.STORE_PRE_DESIGN_QUOTATION:
			return {
				...state,
				preDesignQuotationLoading: false,
				preDesignQuotation: payload
			};

		case ProjectActionType.ERROR_PRE_DESIGN_QUOTATION: {
			return {
				...state,
				preDesignQuotationLoading: false,
				preDesignQuotation: payload.error
			};
		}

		case ProjectActionType.GET_DEFAULT_QUOTATION:
			return {
				...state,
				defaultQuotationsLoading: true,
				defaultQuotations: {}
			};

		case ProjectActionType.STORE_DEFAULT_QUOTATION:
			return {
				...state,
				defaultQuotations: payload,
				defaultQuotationsLoading: false
			};

		case ProjectActionType.CREATE_QUOTATION:
			return {
				...state,
				createQuotationLoading: true
			};

		case ProjectActionType.CREATE_QUOTATION_LOADING:
			return {
				...state,
				createQuotationLoading: payload
			};

		case ProjectActionType.ERROR_DEFAULT_QUOTATION: {
			return {
				...state,
				defaultQuotationsLoading: false,
				defaultQuotations: payload.error
			};
		}

		case ProjectActionType.SET_PROJECT_DETAILS:
			return {
				...state,
				projectId: payload.projectId,
				projectName: payload.projectName,
				// floorPlanDocURL: payload?.attachments[0]?.documentUrl,
				budget: payload?.budget
			};

		case ProjectActionType.RESET_QUOTATION_DATA:
			return {
				...state,
				quotationData: initialState.quotationData,
				preDesignQuotation: initialState.preDesignQuotation,
				defaultQuotations: initialState.defaultQuotations,
				defaultQuotationsLoading: initialState.defaultQuotationsLoading,
				preDesignQuotationLoading: initialState.preDesignQuotationLoading,
				productCategories: initialState.productCategories,
				productCategoriesLoading: initialState.productCategoriesLoading,
				twoDJson: initialState.twoDJson,
				threeDJson: initialState.threeDJson
			};

		case ProjectActionType.RESET_NEW_PROJECT_FLOW:
			return {
				...state,
				productCategories: initialState.productCategories,
				productCategoriesLoading: initialState.productCategoriesLoading,
				productCategoriesError: initialState.productCategoriesError,
				projectActivities: initialState.projectActivities,
				quotationData: initialState.quotationData,
				preDesignQuotation: initialState.preDesignQuotation,
				defaultQuotations: initialState.defaultQuotations,
				defaultQuotationsLoading: initialState.defaultQuotationsLoading,
				preDesignQuotationLoading: initialState.preDesignQuotationLoading,
				projectId: initialState.projectId,
				projectName: initialState.projectName,
				predictionId: initialState.predictionId,
				predictionStatus: initialState.predictionStatus,
				isPolling: initialState.isPolling,
				predictionData: initialState.predictionData,
				budget: initialState.budget,
				floorPlanImages: initialState.floorPlanImages,
				selectedQuotation: initialState.selectedQuotation,
				createQuotationLoading: initialState.createQuotationLoading,
				projectDetails: initialState.projectDetails,
				projectDetailsLoading: initialState.projectDetailsLoading,
				projectDetailsError: initialState.projectDetailsError,
				twoDJson: initialState.twoDJson,
				twoDJsonLoading: initialState.twoDJsonLoading,
				twoDJsonError: initialState.twoDJsonError,
				threeDJson: initialState.threeDJson,
				threeDJsonLoading: initialState.threeDJsonLoading,
				threeDJsonError: initialState.threeDJsonError,
				userTier: initialState.userTier,
				stylePreferences: initialState.stylePreferences
			};

		case ProjectActionType.SEND_CUSTOMER_OTP:
		case ProjectActionType.VERIFY_CUSTOMER_OTP:
			return {
				...state,
				loading: true
			};

		case ProjectActionType.SEND_CUSTOMER_OTP_FAILED:
		case ProjectActionType.VERIFY_CUSTOMER_OTP_FAILED:
			return {
				...state,
				loading: false,
				error: true
			};

		case ProjectActionType.SEND_CUSTOMER_OTP_SUCCESS:
			return {
				...state,
				sessionID: payload.session_id,
				leadId: payload.lead_id,
				loading: false
			};

		case ProjectActionType.SET_LEAD_NAME:
			return {
				...state,
				leadName: payload
			};

		case ProjectActionType.STORE_AREAS: {
			return {
				...state,
				predictionStatus: 'succeeded',
				predictionData: payload?.output?.analysisJson?.areas,
				isPolling: false
			};
		}

		case ProjectActionType.STORE_PREDICTION_ID:
			return {
				...state,
				// API returns the full prediction object; persist only the id string.
				predictionId:
					typeof payload === 'string' || typeof payload === 'number'
						? String(payload)
						: (payload?.id ?? ''),
				floorPlanDocURL: payload?.input?.images?.[0] || state.floorPlanDocURL
			};

		case ProjectActionType.FETCH_PROJECT_DETAILS: {
			return {
				...state,
				projectDetailsLoading: true,
				projectDetails: {},
				projectDetailsError: {}
			};
		}

		case ProjectActionType.STORE_PROJECT_DETAILS: {
			return {
				...state,
				projectDetailsLoading: false,
				projectDetails: payload?.data
			};
		}

		case ProjectActionType.UPDATE_PROJECT_VIEW_FLAGS: {
			return {
				...state,
				projectDetails: state.projectDetails
					? {
							...state.projectDetails,
							...payload
						}
					: state.projectDetails
			};
		}

		case ProjectActionType.FETCH_PROJECT_ACTIVITIES: {
			return {
				...state,
				projectActivities: [],
				projectActivitiesLoading: true,
				projectActivitiesError: { code: '', message: '', type: '' }
			};
		}

		case ProjectActionType.STORE_PROJECT_ACTIVITIES: {
			return {
				...state,
				projectActivitiesLoading: false,
				projectActivities: payload
			};
		}

		case ProjectActionType.ERROR_PROJECT_ACTIVITIES: {
			return {
				...state,
				projectActivitiesLoading: false,
				projectActivitiesError: payload.error
			};
		}

		case ProjectActionType.FETCH_ACTIVITY_DOCUMENTS: {
			return {
				...state,
				projectActivityDocuments: [],
				projectActivityDocumentsLoading: true,
				projectActivityDocumentsError: { code: '', message: '', type: '' }
			};
		}

		case ProjectActionType.STORE_ACTIVITY_DOCUMENTS: {
			return {
				...state,
				projectActivityDocumentsLoading: false,
				projectActivityDocuments: payload
			};
		}

		case ProjectActionType.ERROR_FETCH_ACTIVITY_DOCUMENTS: {
			return {
				...state,
				projectActivityDocumentsLoading: false,
				projectActivityDocumentsError: payload.error
			};
		}

		case ProjectActionType.RESET_ACTIVITY_DOCUMENTS: {
			return {
				...state,
				projectActivityDocuments: [],
				projectActivityDocumentsLoading: false,
				projectActivityDocumentsError: { code: '', message: '', type: '' }
			};
		}

		case ProjectActionType.STORE_CURRENT_ACTIVITY: {
			return {
				...state,
				projectCurrentActivity: payload
			};
		}

		case ProjectActionType.FETCH_ALL_PROJECTS: {
			return {
				...state,
				projectsListLoading: true,
				projectsList: [],
				projectsListError: false
			};
		}

		case ProjectActionType.STORE_ALL_PROJECTS: {
			return {
				...state,
				projectsList: payload.projectsList,
				projectTotalRecords: payload.totalRecords,
				projectsListLoading: false
			};
		}

		case ProjectActionType.ERROR_FETCH_ALL_PROJECTS: {
			return {
				...state,
				projectsListLoading: false,
				projectsListError: payload.error
			};
		}

		case ProjectActionType.FETCH_PROJECT_PRODUCT_CATEGORIES: {
			return {
				...state,
				productCategories: [],
				productCategoriesLoading: true,
				productCategoriesError: { code: '', message: '', type: '' }
			};
		}

		case ProjectActionType.STORE_PROJECT_PRODUCT_CATEGORIES: {
			return {
				...state,
				productCategories: payload.areas,
				productCalculationId: payload?.calculationId,
				productCategoriesLoading: false
			};
		}

		case ProjectActionType.ERROR_PROJECT_PRODUCT_CATEGORIES: {
			return {
				...state,
				productCategoriesError: payload.error,
				productCategoriesLoading: false
			};
		}

		case ProjectActionType.FETCH_2D_JSON: {
			return {
				...state,
				twoDJsonLoading: true
			};
		}

		case ProjectActionType.STORE_2D_JSON: {
			return {
				...state,
				twoDJsonLoading: false,
				twoDJson: payload
			};
		}

		case ProjectActionType.ERROR_FETCH_2D_JSON: {
			return {
				...state,
				twoDJsonLoading: false,
				twoDJsonError: payload.error
			};
		}

		case ProjectActionType.FETCH_3D_JSON: {
			return {
				...state,
				threeDJsonLoading: true,
				threeDJson: {}
			};
		}

		case ProjectActionType.STORE_3D_JSON: {
			// Handle both old format (payload is data directly) and new format
			// (payload is { data, projectId, currentFloor }).
			const data = payload?.data || payload;
			const projectId = payload?.projectId;
			const currentFloor = payload?.currentFloor;

			const updatedState: any = {
				...state,
				threeDJsonLoading: false,
				threeDJson: data // Keep for backward compatibility
			};

			// Store 3D JSON per projectId
			if (projectId) {
				updatedState.threeDJsonByProjectId = {
					...state.threeDJsonByProjectId,
					[projectId]: data,
					[`${projectId}:${currentFloor ?? 0}`]: data
				};
			}

			return updatedState;
		}

		case ProjectActionType.ERROR_FETCH_3D_JSON: {
			return {
				...state,
				threeDJsonLoading: false,
				threeDJsonError: payload.error
			};
		}
		case ProjectActionType.FETCH_LEAD_PROJECTS: {
			return {
				...state,
				leadProjectsListLoading: true
			};
		}

		case ProjectActionType.STORE_LEAD_PROJECTS: {
			return {
				...state,
				leadProjectsListLoading: false,
				leadProjectsList: payload.projects
			};
		}

		case ProjectActionType.STORE_QUOTATION_AREA_LIST_DROPDOWN: {
			return {
				...state,
				areaList: payload
			};
		}

		case ProjectActionType.FETCH_PAYMENT_SLIPS: {
			return {
				...state,
				paymentSlipsListLoading: true
			};
		}

		case ProjectActionType.STORE_PAYMENT_SLIPS: {
			return {
				...state,
				paymentSlipsListLoading: false,
				paymentSlipsList: payload
			};
		}

		case ProjectActionType.ERROR_PAYMENT_SLIPS: {
			return {
				...state,
				paymentSlipsListLoading: false
			};
		}

		case ProjectActionType.FETCH_CUSTOMER_SLIPS: {
			return {
				...state,
				customerSlipsListLoading: true
			};
		}

		case ProjectActionType.STORE_CUSTOMER_SLIPS: {
			return {
				...state,
				customerSlipsListLoading: false,
				customerSlipsList: payload.data
			};
		}

		case ProjectActionType.ERROR_CUSTOMER_SLIPS: {
			return {
				...state,
				customerSlipsListLoading: false
			};
		}

		case ProjectActionType.FETCH_LEDGER_DATA: {
			return {
				...state,
				ledgerDataLoading: true,
				ledgerData: {},
				ledgerDataError: {
					code: '',
					message: '',
					type: ''
				}
			};
		}

		case ProjectActionType.STORE_LEDGER_DATA: {
			return {
				...state,
				ledgerDataLoading: false,
				ledgerData: payload
			};
		}

		case ProjectActionType.ERROR_LEDGER_DATA: {
			return {
				...state,
				ledgerDataLoading: false,
				ledgerDataError: payload.error
			};
		}

		case ProjectActionType.FETCH_ALL_PAYMENTS: {
			return {
				...state,
				isPaymentLoading: true
			};
		}

		case ProjectActionType.STORE_ALL_PAYMENTS: {
			return {
				...state,
				isPaymentLoading: false,
				paymentList: payload.data
			};
		}
		case ProjectActionType.ERROR_ALL_PAYMENTS: {
			return {
				...state,
				isPaymentLoading: false
			};
		}
		case ProjectActionType.GET_WARRANTY_CLAIMS_BY_LEAD: {
			return {
				...state,
				warrantyClaimsLoading: true,
				warrantyClaimsError: null
			};
		}

		case ProjectActionType.STORE_WARRANTY_CLAIMS_BY_LEAD: {
			return {
				...state,
				warrantyClaimsLoading: false,
				warrantyClaims: payload.data || [],
				warrantyClaimsTotalRecords: payload.total_records || 0
			};
		}

		case ProjectActionType.ERROR_GET_WARRANTY_CLAIMS_BY_LEAD: {
			return {
				...state,
				warrantyClaimsLoading: false,
				warrantyClaimsError: payload.error
			};
		}

		case ProjectActionType.FETCH_ALL_WARRANTY: {
			return {
				...state,
				warrantiesLoading: true,
				warrantiesError: null
			};
		}

		case ProjectActionType.STORE_ALL_WARRANTY: {
			return {
				...state,
				warrantiesLoading: false,
				warranties: payload.data || [],
				warrantiesTotalRecords: payload.total_records || 0
			};
		}

		case ProjectActionType.ERROR_FETCH_ALL_WARRANTY: {
			return {
				...state,
				warrantiesLoading: false,
				warrantiesError: payload.error
			};
		}

		case ProjectActionType.FETCH_WARRANTY_DETAILS: {
			return {
				...state,
				warrantyCodeDetailsLoading: true,
				warrantyCodeDetailsError: null,
				warrantyCodeDetails: []
			};
		}

		case ProjectActionType.STORE_WARRANTY_DETAILS: {
			return {
				...state,
				warrantyCodeDetailsLoading: false,
				warrantyCodeDetails: payload
			};
		}

		case ProjectActionType.ERROR_WARRANTY_DETAILS: {
			return {
				...state,
				warrantyCodeDetailsLoading: false,
				warrantyCodeDetailsError: payload?.error || payload
			};
		}

		case ProjectActionType.GET_WARRANTY_CLAIM: {
			return {
				...state,
				warrantyClaimDetailsLoading: true,
				warrantyClaimDetailsError: null
			};
		}

		case ProjectActionType.STORE_WARRANTY_CLAIM_DETAILS: {
			return {
				...state,
				warrantyClaimDetailsLoading: false,
				warrantyClaimDetails: payload
			};
		}

		case ProjectActionType.ERROR_GET_WARRANTY_CLAIM: {
			return {
				...state,
				warrantyClaimDetailsLoading: false,
				warrantyClaimDetailsError: payload.error
			};
		}

		case ProjectActionType.UPDATE_WARRANTY_PAYMENT: {
			return {
				...state,
				warrantyClaimDetailsLoading: true
			};
		}

		case ProjectActionType.STORE_WARRANTY_PAYMENT_UPDATE: {
			return {
				...state,
				warrantyClaimDetailsLoading: false,
				warrantyClaimDetails: state.warrantyClaimDetails
					? {
							...state.warrantyClaimDetails,
							paymentApprovalId: payload.paymentApprovalId
						}
					: null
			};
		}

		case ProjectActionType.ERROR_UPDATE_WARRANTY_PAYMENT: {
			return {
				...state,
				warrantyClaimDetailsLoading: false,
				warrantyClaimDetailsError: payload.error
			};
		}

		case ProjectActionType.FETCH_CUTLIST_DATA: {
			return {
				...state,
				cutListData: [],
				cutListDataLoading: true,
				cutListDataError: {}
			};
		}

		case ProjectActionType.STORE_CUTLIST_DATA: {
			return {
				...state,
				cutListData: payload.cutlist,
				cutListDataLoading: false
			};
		}

		case ProjectActionType.ERROR_CUTLIST_DATA: {
			return {
				...state,
				cutListDataLoading: false,
				cutListDataError: payload?.error
			};
		}

		case ProjectActionType.FETCH_OPTIMISER_DATA: {
			return {
				...state,
				optimiserData: {},
				optimiserDataLoading: true,
				optimiserDataError: null
			};
		}

		case ProjectActionType.FETCH_OPTIMIZED_CUTLIST_DATA: {
			return {
				...state,
				optimiserData: {},
				optimiserDataLoading: true,
				optimiserDataError: null
			};
		}

		case ProjectActionType.STORE_OPTIMISER_DATA: {
			return {
				...state,
				optimiserData: payload?.data?.data,
				optimiserDataLoading: false,
				optimiserDataError: null
			};
		}

		case ProjectActionType.ERROR_OPTIMISER_DATA: {
			return {
				...state,
				optimiserDataLoading: false,
				optimiserDataError: payload?.error
			};
		}

		case ProjectActionType.FETCH_ASSIGNED_PROJECTS: {
			return {
				...state,
				assignedProjectsLoading: true,
				assignedProjectsError: null
			};
		}

		case ProjectActionType.STORE_ASSIGNED_PROJECTS: {
			return {
				...state,
				assignedProjectIds: payload?.projectIds || [],
				assignedProjectsLoading: false,
				assignedProjectsError: null
			};
		}

		case ProjectActionType.ERROR_ASSIGNED_PROJECTS: {
			return {
				...state,
				assignedProjectsLoading: false,
				assignedProjectsError: payload?.error
			};
		}

		case ProjectActionType.FETCH_BUILDER_STATES: {
			return {
				...state,
				builderStatesLoading: true,
				builderStatesError: null
			};
		}

		case ProjectActionType.STORE_BUILDER_STATES: {
			return {
				...state,
				builderStates: payload.builderStates || [],
				builderStatesLoading: false,
				builderStatesError: null
			};
		}

		case ProjectActionType.FETCH_PROMO_CODE_HISTORY: {
			return {
				...state,
				promoCodeHistoryLoading: true,
				promoCodeHistoryError: null
			};
		}

		case ProjectActionType.STORE_PROMO_CODE_HISTORY: {
			const history = Array.isArray(payload)
				? payload
				: Array.isArray(payload?.history)
					? payload.history
					: [];
			return {
				...state,
				promoCodeHistory: history,
				promoCodeHistoryLoading: false,
				promoCodeHistoryError: null
			};
		}

		case ProjectActionType.ERROR_PROMO_CODE_HISTORY: {
			return {
				...state,
				promoCodeHistoryLoading: false,
				promoCodeHistoryError: payload.error
			};
		}

		case ProjectActionType.CALCULATE_PROMO_CODE_AMOUNT: {
			return {
				...state,
				promoCodeAmountLoading: true,
				promoCodeAmountError: null
			};
		}

		case ProjectActionType.STORE_PROMO_CODE_AMOUNT: {
			return {
				...state,
				promoCodeAmount: payload,
				promoCodeAmountLoading: false,
				promoCodeAmountError: null
			};
		}

		case ProjectActionType.ERROR_PROMO_CODE_AMOUNT: {
			return {
				...state,
				promoCodeAmountLoading: false,
				promoCodeAmountError: payload.error
			};
		}

		case ProjectActionType.FETCH_LEAD_PROJECTS_ORDERS: {
			return {
				...state,
				projectsOrdersLoading: true,
				projectsOrdersError: null
			};
		}

		case ProjectActionType.STORE_LEAD_PROJECTS_ORDERS: {
			return {
				...state,
				projectsOrders: payload,
				projectsOrdersLoading: false,
				projectsOrdersError: null
			};
		}

		case ProjectActionType.ERROR_LEAD_PROJECTS_ORDERS: {
			return {
				...state,
				projectsOrdersLoading: false,
				projectsOrdersError: payload.error
			};
		}

		case ProjectActionType.SET_USER_TIER: {
			return {
				...state,
				userTier: payload
			};
		}

		case ProjectActionType.SET_STYLE_PREFERENCES: {
			return {
				...state,
				stylePreferences: {
					...state.stylePreferences,
					...payload
				}
			};
		}

		default:
			return state;
	}
};
